<template>
    <q-page padding>
        <!-- Modern Table Card -->
        <q-card class="obras-table-card">
            <q-table
                ref="tableRef"
                dense
                :rows="rows"
                :columns="columns"
                row-key="id"
                v-model:pagination="pagination"
                :filter="filter"
                @request="onRequest"
                class="modern-obras-table sticky-header-table"
                flat
            >
                <template v-slot:top-left>
                    <div class="page-title">
                        <q-icon name="engineering" class="q-mr-md" size="lg" />
                        <div>
                            <div class="text-h6">Obras</div>
                            <div class="text-caption text-grey-7">Gestión de obras y proyectos</div>
                        </div>
                    </div>
                </template>
                <template v-slot:top-right>
                    <div class="toolbar-actions">
                        <q-input
                            v-model="filter"
                            placeholder="Buscar obras..."
                            class="search-input"
                            outlined
                            dense
                            debounce="300"
                        >
                            <template v-slot:prepend>
                                <q-icon name="search" />
                            </template>
                            <template v-slot:append>
                                <q-icon v-if="filter" name="close" @click="filter = ''" class="cursor-pointer" />
                            </template>
                        </q-input>
                    </div>
                </template>
                <template v-slot:body-cell-observaciones="props">
                    <q-td :props="props">
                        <div class="obs-cell">
                            <span class="obs-text">{{ props.row.observaciones }}</span>
                        </div>
                    </q-td>
                </template>
                <template v-slot:body-cell-direccion="props">
                    <q-td :props="props">
                        <div class="direccion-cell">
                            <span class="direccion-text">{{ props.row.direccion }}</span>
                        </div>
                    </q-td>
                </template>
                <template v-slot:body-cell-acciones="props">
                    <q-td :props="props">
                        <div class="action-buttons">
                            <q-btn
                                color="primary"
                                :loading="editLoading === props.row.id"
                                round
                                flat
                                icon="edit"
                                size="sm"
                                @click="edit(props.row.id)"
                                class="action-btn"
                            >
                                <q-tooltip>Editar obra</q-tooltip>
                            </q-btn>
                            <q-btn
                                color="amber"
                                round
                                flat
                                icon="assignment_ind"
                                size="sm"
                                @click="presupuestosShow(props.row.id)"
                                class="action-btn"
                            >
                                <q-tooltip>Ver presupuestos</q-tooltip>
                            </q-btn>
                        </div>
                    </q-td>
                </template>
            </q-table>
        </q-card>
        <ObraCrud
            v-model="obraCrudOpen"
            :oper="obraCrudOper"
            :id="obraId"
            @updated="tableRefresh"
        />
        <PresupuestosDialog v-model="presupuestosDialogOpen" :obra="obraId" />
        <q-inner-loading :showing="loading">
            <q-spinner-hourglass size="50px" color="primary" />
        </q-inner-loading>
    </q-page>
</template>

<script setup>
import ObraCrud from "@/components/obra/ObraCrud.vue";
import ObraService from "@/services/ObraService.js";
import PresupuestosDialog from "@/pages/Obras/PresupuestosDialog.vue";
import { ref, onMounted } from "vue";
import { useQuasar } from "quasar";

const presupuestosDialogOpen = ref(false);
const filter = ref("");
const tableRef = ref();
const loading = ref(false);
const $q = useQuasar();
const pagination = ref({
    sortBy: "id",
    descending: true,
    page: 1,
    rowsPerPage: 100,
    rowsNumber: 10,
    cliente: true,
});
const columns = [
    { name: "id", label: "ID", field: "id", align: "left", sortable: true },
    {
        name: "referencia",
        label: "Referencia",
        field: "referencia",
        align: "left",
        sortable: true,
    },
    {
        label: "Cliente",
        name: "cliente",
        align: "left",
        field: "A1_NOME",
        sortable: true,
    },
    {
        label: "Distribuidor",
        name: "distribuidor",
        align: "left",
        field: "A3_NOME",
        sortable: true,
    },
    {
        label: "Direccion",
        name: "direccion",
        align: "left",
        field: "direccion",
    },
    {
        label: "Observaciones",
        name: "observaciones",
        align: "left",
        field: "observaciones",
    },
    {
        label: "Acciones",
        name: "acciones",
        align: "center",
    },
];
const rows = ref([]);
const onRequest = async (props) => {
    const filter = props.filter;
    const { page, rowsPerPage, sortBy, descending } = props.pagination;
    loading.value = true;

    try {
        const { data, per_page, current_page, total } = await ObraService.index({
            page,
            rowsPerPage,
            sortBy,
            descending,
            filter,
            cliente: true,
        });
        rows.value = data;
        pagination.value.rowsPerPage = per_page;
        pagination.value.page = current_page;
        pagination.value.rowsNumber = total;
        pagination.value.sortBy = sortBy;
        pagination.value.descending = descending;
    } catch (error) {
        $q.notify({
            type: "negative",
            message: "Error al cargar las obras",
        });
        console.error("Error loading obras:", error);
    }
    loading.value = false;
};
onMounted(() => {
    // get initial data from server (1st page)
    tableRef.value.requestServerInteraction();
});
const tableRefresh = () => tableRef.value.requestServerInteraction();

const editLoading = ref(false);
const obraCrudOpen = ref(false);
const obraCrudOper = ref("edit");
const obraId = ref(null);
const edit = async (id) => {
    editLoading.value = id;
    obraId.value = id;
    obraCrudOper.value = "edit";
    obraCrudOpen.value = true;
    editLoading.value = false;
};

const presupuestosShow = (id) => {
    presupuestosDialogOpen.value = true;
    obraId.value = id;
};
</script>

<style lang="scss" scoped>
// Modern Table Card
.obras-table-card {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

    .modern-obras-table {
        :deep(.q-table__top) {
            padding: 16px;
            background: linear-gradient(to right, #f5f5f5, #fafafa);
            border-bottom: 2px solid #e0e0e0;
        }

        :deep(.q-table thead th) {
            font-weight: 600;
            font-size: 13px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            background: #fafafa;
            color: #424242;
        }

        :deep(.q-table tbody tr) {
            transition: all 0.2s ease;

            &:hover {
                background: #f5f9ff;
                transform: translateX(2px);
            }
        }

        :deep(.q-table tbody td) {
            border-bottom: 1px solid #f0f0f0;
        }
    }
}




// Cell Styles
.obs-cell {
    display: flex;
    align-items: center;

    .obs-text {
        font-size: 13px;
        color: #424242;
    }
}

.direccion-cell {
    display: flex;
    align-items: center;

    .direccion-text {
        font-size: 13px;
        color: #424242;
    }
}

.action-buttons {
    display: flex;
    gap: 4px;
    justify-content: center;

    .action-btn {
        transition: all 0.2s ease;

        &:hover {
            transform: scale(1.1);
        }
    }
}


</style>
